import React, { useEffect, useMemo, useRef, useState } from 'react'
import injectSheet from 'react-jss'
import { printPageStyles, printPasscodesStyles } from './print-passcodes.styles'
import { Modal, ModalContent } from 'pobble-components/dist/modal'
import Button from 'pobble-components/dist/button'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPrint } from '@fortawesome/pro-solid-svg-icons/faPrint'
import { faTimesCircle } from '@fortawesome/pro-solid-svg-icons/faTimesCircle'
import { apiGetAccount } from '../../common/api/profile'
import { useReactToPrint } from 'react-to-print'
import printPasscodesImg from '../../common/images/print-passcodes.png'
import ButtonElement from 'pobble-components/dist/button-element'

export const usePasscodesPrint = ({ ids, accounts, onAfterPrint } = {}) => {
  const [pupils, setPupils] = useState(null)
  const [printInProgress, setPrintInProgress] = useState(false)
  const componentRef = useRef(null)

  const pupilsPages = useMemo(
    () => {
      if (!pupils) return null
      const pupilsPerPage = 8
      const result = []
      for (let i = 0; i < pupils.length; i += pupilsPerPage) {
        let page = pupils.slice(i, i + pupilsPerPage)
        if (page.length < pupilsPerPage)
          page = [...page, ...new Array(pupilsPerPage - page.length)]
        result.push(page)
      }
      return result
    },
    [pupils]
  )

  useEffect(
    () => {
      if (accounts) {
        setPupils(accounts)
      }
    },
    [accounts]
  )

  useEffect(
    () => {
      if (ids && !!ids.length) {
        Promise.all(
          ids.map(id =>
            apiGetAccount(id, {
              include: 'account.names,account.student_type.passcode'
            }).then(res => res.data.account)
          )
        ).then(setPupils)
      }
    },
    [ids]
  )

  const handlePrint = useReactToPrint({
    onAfterPrint: () => {
      setPrintInProgress(false)
      onAfterPrint && onAfterPrint()
    },
    copyStyles: true,
    pageStyle:
      '@page { size: portrait;  margin: 0mm; } @media print { body { -webkit-print-color-adjust: exact; } }"',
    content: () => componentRef.current
  })

  const print = () => {
    setPrintInProgress(true)
    setTimeout(handlePrint, 1000)
  }

  const PrintComponent = injectSheet(printPageStyles)(({ classes }) => {
    const renderItem = pupil =>
      !pupil ? (
        <td />
      ) : (
        <td className={classes.item}>
          <p className={classes.fullName}>{pupil.full_name}</p>
          <div className={classes.nameField}>
            <span className={classes.label}>Name</span>
            <span className={classes.name}>{pupil.first_name}</span>
          </div>
          <div>
            <span className={classes.label}>Passcode</span>
            <div className={classes.passcode}>
              {pupil.student_type.passcode.split('').map(number => (
                <span>{number}</span>
              ))}
            </div>
          </div>
        </td>
      )
    return !printInProgress ? null : (
      <div className={classes.container} ref={componentRef}>
        {pupilsPages.map(page => {
          return (
            <table className={classes.page}>
              <tbody>
                {page.map((pupil, index) => {
                  const nextPupil = page[index + 1]
                  return (
                    index % 2 === 0 && (
                      <tr>
                        {renderItem(pupil)}
                        {renderItem(nextPupil)}
                      </tr>
                    )
                  )
                })}
              </tbody>
            </table>
          )
        })}
      </div>
    )
  })
  return { PrintComponent, print, printInProgress, pupils }
}

const PrintPasscodes = injectSheet(printPasscodesStyles)(
  ({ classes, ids, requestClose }) => {
    const {
      print,
      PrintComponent,
      printInProgress,
      pupils
    } = usePasscodesPrint({
      ids,
      onAfterPrint: () => requestClose()
    })

    return (
      !!ids && (
        <ModalContent legacy className={classes.content}>
          <ButtonElement onClick={requestClose} className={classes.closeButton}>
            <FontAwesomeIcon icon={faTimesCircle} />
          </ButtonElement>
          <div className={classes.img}>
            <img src={printPasscodesImg} alt="Print pupil passcodes" />
          </div>
          <div className={classes.desc}>
            <h2>Print pupil passcodes</h2>
            <p>
              You are about to print {ids.length} passcode
              {ids.length > 1 && 's'}
            </p>
            <Button
              action={print}
              icon={<FontAwesomeIcon icon={faPrint} />}
              disabled={!pupils}
              pending={!pupils || printInProgress}
              className={classes.button}
              tall>
              Print
            </Button>
          </div>
          <PrintComponent />
        </ModalContent>
      )
    )
  }
)

export const PrintPasscodesDialog = ({
  classes,
  isOpen,
  requestClose,
  ...props
}) => {
  return (
    <Modal closeButton isOpen={isOpen} requestClose={requestClose}>
      <PrintPasscodes {...props} requestClose={requestClose} />
    </Modal>
  )
}
